import { create } from 'zustand';
import { supabase } from '../supabase';
import { useAuthStore } from './useAuthStore';
import { useOfflineQueue } from './useOfflineQueue';
import { usePieceStore } from './usePieceStore';

// Production-grade: All logic in database RPC functions
// No Edge Functions - everything uses RPC

interface SewingState {
    loading: boolean;
    error: string | null;
    
    assignPieceToTailor: (sampleId: string, pieceId: string, tailorId: string, tailorName: string, startDate?: string) => Promise<void>;
    assignTailorsBulk: (assignments: Array<{pieceId: string, tailorId: string, tailorName: string}>) => Promise<void>;
    completePieceSewing: (sampleId: string, pieceId: string, endDate?: string) => Promise<void>;
    completeSewingBulk: (pieceIds: string[], endDate?: string) => Promise<void>;
}

export const useSewingStore = create<SewingState>((set) => ({
    loading: false,
    error: null,

    assignTailorsBulk: async (assignments) => {
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('assignTailorsBulk', { assignments });
            return;
        }
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;

        set({ loading: true, error: null });
        try {
            const idempotencyKey = `assign_tailors_${Date.now()}`;
            const assignmentsJson = assignments.map(a => ({
                piece_id: a.pieceId,
                tailor_id: a.tailorId,
                tailor_name: a.tailorName
            }));

            const { data, error } = await supabase.rpc('rpc_assign_tailors_bulk', {
                p_factory_id: u.factory_id,
                p_assignments: assignmentsJson,
                p_user_id: u.id,
                p_user_role: u.role,
                p_assigned_at: new Date().toISOString(),
                p_idempotency_key: idempotencyKey
            });

            if (error) throw error;
            if (!data.success) throw new Error(data.error_message);

            console.log(`✅ Tailors assigned: ${data.affected_count} pieces`);
            await usePieceStore.getState().fetchAssignedSamples();

        } catch (e: any) {
            console.error('❌ assignTailorsBulk:', e);
            set({ error: e.message });
            throw e;
        } finally {
            set({ loading: false });
        }
    },

    assignPieceToTailor: async (_sampleId, pieceId, tailorId, tailorName, startDate) => {
        // Single-piece wrapper - uses bulk RPC internally
        console.log('[assignPieceToTailor] Called with:', { pieceId, tailorId, tailorName, startDate });
        try {
            const { assignTailorsBulk } = useSewingStore.getState();
            await assignTailorsBulk([{ pieceId, tailorId, tailorName }]);
            console.log('[assignPieceToTailor] ✅ Success');
        } catch (error) {
            console.error('[assignPieceToTailor] ❌ Error:', error);
            throw error;
        }
    },

    completeSewingBulk: async (pieceIds, endDate) => {
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('completeSewingBulk', { pieceIds, endDate });
            return;
        }
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;

        set({ loading: true, error: null });
        try {
            const idempotencyKey = `complete_sewing_${Date.now()}`;

            const { data, error } = await supabase.rpc('rpc_complete_sewing_bulk', {
                p_factory_id: u.factory_id,
                p_piece_ids: pieceIds,
                p_user_id: u.id,
                p_user_role: u.role,
                p_completed_at: endDate || new Date().toISOString(),
                p_idempotency_key: idempotencyKey
            });

            if (error) throw error;
            if (!data.success) throw new Error(data.error_message);

            console.log(`✅ Sewing completed: ${data.affected_count} pieces`);
            await usePieceStore.getState().fetchAssignedSamples();

        } catch (e: any) {
            console.error('❌ completeSewingBulk:', e);
            set({ error: e.message });
            throw e;
        } finally {
            set({ loading: false });
        }
    },

    completePieceSewing: async (_sampleId, pieceId, endDate) => {
        // Single-piece wrapper - uses bulk RPC internally
        const { completeSewingBulk } = useSewingStore.getState();
        await completeSewingBulk([pieceId], endDate);
    },
}));
